import React, { useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { notification } from 'antd';

import { getOptionsManufacture, getOptionsOrigin, getOptionsUnit } from 'services/product.service';
import { mapDataOptions4Select } from 'utils/helpers';
import { getOptionsModel, getOptionsTreeview } from 'services/product-category.service';
import FilterSearchBar from 'components/shared/FilterSearchBar/index';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormDebouneSelect from 'components/shared/BWFormControl/FormDebouneSelect';
import FormTreeSelect from 'components/shared/BWFormControl/FormTreeSelect';

const getSelectValue = (value) => {
  if (value && typeof value === 'object' && !React.isValidElement(value)) {
    return value.value ?? value.id ?? value.key ?? null;
  }

  return value ?? null;
};

const getModalPopupContainer = (triggerNode) => triggerNode?.closest?.('.bw_modal_container') || document.body;

const PromotionProductFilter = ({ onChange, miniForm = false }) => {
  const methods = useForm();
  const [optionsUnit, setOptionsUnit] = useState([]);
  const [optionsOrigin, setOptionsOrigin] = useState([]);
  const [optionsManufacture, setOptionsManufacture] = useState([]);
  const [optionsModel, setOptionsModel] = useState([]);
  const productCategoryId = getSelectValue(methods.watch('product_category_id'));

  const fetchOptionsUnit = useCallback((search) => getOptionsUnit({ search, limit: 100 }), []);
  const fetchOptionsOrigin = useCallback((search) => getOptionsOrigin({ search, limit: 100 }), []);
  const fetchOptionsManufacture = useCallback((search) => getOptionsManufacture({ search, limit: 100 }), []);

  const getProductModel = useCallback(async (search = '') => {
    try {
      let models = [];
      if (productCategoryId) {
        const res = await getOptionsModel({ search, limit: 100, productCategoryId });
        models = mapDataOptions4Select(res);
      }
      return models;
    } catch (error) {
      notification.error({ message: error.message || 'Lỗi lấy model sản phẩm.' });
    }
  }, [productCategoryId]);

  const loadData = useCallback(async () => {
    try {
      const units = await fetchOptionsUnit();
      const origins = await fetchOptionsOrigin();
      const manufactures = await fetchOptionsManufacture();

      setOptionsUnit(mapDataOptions4Select(units));
      setOptionsOrigin(mapDataOptions4Select(origins));
      setOptionsManufacture(mapDataOptions4Select(manufactures));
    } catch (error) {
      notification.error({ message: error.message || 'Lỗi lấy dữ liệu.' });
    }
  }, [fetchOptionsManufacture, fetchOptionsOrigin, fetchOptionsUnit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    methods.setValue('model_id', null, { shouldDirty: true, shouldValidate: true });
    setOptionsModel([]);

    if (productCategoryId) {
      getProductModel().then((res) => setOptionsModel(res || []));
    }
  }, [getProductModel, methods, productCategoryId]);

  const onSubmit = () => {
    onChange({
      search: methods.getValues('search'),
      product_category_id: productCategoryId,
      model_id: getSelectValue(methods.getValues('model_id')),
      is_active: methods.getValues('is_active') ?? 1,
      unit_id: getSelectValue(methods.getValues('unit_id')),
      origin_id: getSelectValue(methods.getValues('origin_id')),
      manufacture_id: getSelectValue(methods.getValues('manufacture_id')),
      page: 1,
    });
  };

  const onClear = () => {
    const rsvalues = {
      search: '',
      product_category_id: null,
      model_id: null,
      is_active: 1,
      unit_id: null,
      origin_id: null,
      manufacture_id: null,
      page: 1,
    };

    methods.reset(rsvalues);
    onChange(rsvalues);
  };

  const handleKeyDownSearch = (event) => {
    if (1 * event.keyCode === 13) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <FormProvider {...methods}>
      <FilterSearchBar
        title='Tìm kiếm'
        onSubmit={onSubmit}
        onClear={onClear}
        actions={[
          {
            title: 'Từ khóa',
            component: (
              <FormInput
                onKeyDown={handleKeyDownSearch}
                type='text'
                placeholder='Nhập mã, tên hàng hóa'
                field='search'
              />
            ),
          },
          {
            title: 'Thuộc ngành hàng',
            component: (
              <FormTreeSelect
                field='product_category_id'
                fetchOptions={getOptionsTreeview}
                allowClear
                treeNodeFilterProp='title'
                treeDataSimpleMode
                placeholder='Tất cả'
                getPopupContainer={getModalPopupContainer}
                popupClassName='bw_promotion_product_category_dropdown'
                onChange={() => {
                  methods.setValue('model_id', null, { shouldDirty: true, shouldValidate: true });
                  setOptionsModel([]);
                }}
              />
            ),
          },
          {
            title: 'Model sản phẩm',
            component: (
              <FormDebouneSelect
                field='model_id'
                fetchOptions={getProductModel}
                allowClear={true}
                disabled={!productCategoryId}
                placeholder='Tất cả'
                options={optionsModel}
                getPopupContainer={getModalPopupContainer}
              />
            ),
          },
          {
            title: 'Trạng thái',
            component: <FormInput value='Kích hoạt' disabled />,
            hidden: miniForm,
          },
          {
            title: 'Hãng',
            component: (
              <FormDebouneSelect
                field='manufacture_id'
                fetchOptions={fetchOptionsManufacture}
                allowClear={true}
                placeholder='Tất cả'
                list={optionsManufacture}
                getPopupContainer={getModalPopupContainer}
              />
            ),
          },
          {
            title: 'Đơn vị tính',
            component: (
              <FormDebouneSelect
                field='unit_id'
                fetchOptions={fetchOptionsUnit}
                allowClear={true}
                placeholder='Tất cả'
                list={optionsUnit}
                getPopupContainer={getModalPopupContainer}
              />
            ),
            hidden: miniForm,
          },
          {
            title: 'Xuất xứ',
            component: (
              <FormDebouneSelect
                field='origin_id'
                fetchOptions={fetchOptionsOrigin}
                allowClear={true}
                placeholder='Tất cả'
                list={optionsOrigin}
                getPopupContainer={getModalPopupContainer}
              />
            ),
            hidden: miniForm,
          },
        ]}
      />
    </FormProvider>
  );
};

export default PromotionProductFilter;
